import { setFieldError, setLoading } from "@/stores";
import {
  buildMetaEventContext,
  getAdClickAttribution,
  pushAnalyticsError,
  pushDataLayerEvent,
  resolveSafeNextPath,
  trackMetaEvent,
  withLocale,
} from "@/helpers";
import { registerUser } from "./index";

export type CreatePasswordOptions = {
  /** BCP 47 / app locale string for `account_profiles.locale`. */
  locale?: string;
  /** Safe local continuation (e.g. /oidc/authorize?…). */
  next?: string | null;
};

/**
 * Completes sign-up: calls `POST /auth/register` with nested `account` / `profile` / `consent`
 * (schema v2), then relies on Set-Cookie from the API for the session.
 */
export const createPassword = async (
  values: Record<string, unknown>,
  options?: CreatePasswordOptions,
) => {
  setLoading(true);

  const password = String(values.password ?? "");
  const repeatPassword = String(values.repeatPassword ?? "");
  const termsAccepted = values.termsAccepted === true;

  const meta = buildMetaEventContext();
  const result = await registerUser({
    account: {
      email: String(values.email ?? ""),
      password,
      confirmPassword: repeatPassword,
      accountType: "default",
    },
    profile: {
      firstName: String(values.firstName ?? ""),
      lastName: String(values.lastName ?? ""),
      locale: options?.locale,
    },
    consent: termsAccepted
      ? { acceptTerms: true, acceptPrivacy: true }
      : undefined,
    silent: false,
    attribution: getAdClickAttribution(),
    meta,
  });

  if (result.ok) {
    pushDataLayerEvent({
      event: "form_submit_success",
      form_name: "sign_up_form",
    });
    pushDataLayerEvent({
      event: "signup_success",
      method: "email",
    });
    trackMetaEvent(
      "CompleteRegistration",
      { content_name: "account_created", status: true },
      meta.eventId,
    );

    const locale = options?.locale || "en";
    const fallback = withLocale(locale, "/verification");
    window.location.href = resolveSafeNextPath(options?.next, fallback, locale);
  } else {
    pushAnalyticsError({
      event: "signup_error",
      errorCode: result.code,
      errorMessage: result.message,
      errorStep: "password",
      method: "email",
    });
    // Prefer safe API validation message/code; generic only when unclassified.
    setFieldError(
      "password",
      result.message?.trim() || "Something went wrong. Try again.",
    );
    setLoading(false);
  }
};
